// Gabarits HTML échappés par défaut : toute valeur interpolée est du texte,
// sauf si elle vient elle-même de html`…` (ou de raw()). Pas d'injection
// possible par un prénom, un motif d'absence ou un titre d'événement.

import { releaseFocusIn } from './keyboard';

export class Html { constructor(readonly s: string) {} toString() { return this.s; } }

const ESC: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

function render(v: unknown): string {
  if (v instanceof Html) return v.s;
  if (Array.isArray(v)) return v.map(render).join('');
  if (v === null || v === undefined || v === false) return '';
  return String(v).replace(/[&<>"']/g, c => ESC[c]!);
}

export function html(strings: TemplateStringsArray, ...values: unknown[]): Html {
  let out = strings[0] ?? '';
  values.forEach((v, i) => { out += render(v) + (strings[i + 1] ?? ''); });
  return new Html(out);
}

export const raw = (s: string): Html => new Html(s);

export function mount(el: HTMLElement, content: Html): void {
  releaseFocusIn(el);   // un champ retiré avec la main laisse la page décalée sur iPhone
  el.innerHTML = content.s;
  // Les images de plays reprises de Playbook (D-151) se remplissent après l'affichage.
  if (content.s.includes('data-aimg="')) void import('./play-images').then(m => m.hydratePlayImages(el)).catch(() => undefined);
}

export const $ = <T extends Element = HTMLElement>(root: ParentNode, sel: string): T | null => root.querySelector<T>(sel);
export const $$ = <T extends Element = HTMLElement>(root: ParentNode, sel: string): T[] => [...root.querySelectorAll<T>(sel)];

/**
 * Fiche coulissante en bas d'écran (formulaires, choix). `setup` reçoit la
 * fiche et de quoi la fermer. Échap et le fond la ferment aussi.
 */
export function openSheet(title: string, body: Html, setup?: (sheet: HTMLElement, close: () => void) => void, onClose?: () => void): () => void {
  const host = document.createElement('div');
  host.innerHTML = html`
    <div class="sheet-backdrop" data-close></div>
    <div class="sheet" role="dialog" aria-modal="true" aria-label="${title}">
      <div class="sheet-head"><h3>${title}</h3><button type="button" class="sheet-x" data-close aria-label="Fermer">×</button></div>
      <div class="sheet-body">${body}</div>
    </div>`.s;
  document.body.append(host);
  const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
  const close = () => { releaseFocusIn(host); host.remove(); document.removeEventListener('keydown', onKey); onClose?.(); };
  document.addEventListener('keydown', onKey);
  host.querySelectorAll('[data-close]').forEach(b => b.addEventListener('click', close));
  const sheet = host.querySelector<HTMLElement>('.sheet')!;
  setup?.(sheet, close);
  sheet.querySelector<HTMLElement>('input:not([type=hidden]), select')?.focus();
  return close;
}

/** Bouton à confirmer en deux gestes (actions difficiles à défaire). `onArm` : au premier geste (dire ce qui va changer). */
export function confirmTwice(btn: HTMLButtonElement, confirmLabel: string, action: () => Promise<void>, onArm?: () => void): void {
  btn.addEventListener('click', async () => {
    if (!btn.dataset.armed) { btn.dataset.armed = '1'; btn.textContent = confirmLabel; onArm?.(); return; }
    btn.disabled = true;
    try { await action(); } finally { btn.disabled = false; }
  });
}

/** Action difficile à défaire, dans un gestionnaire existant : le premier geste demande confirmation,
 *  le second agit. `if (!armed(b)) return;` en tête du gestionnaire. */
export function armed(btn: HTMLElement, label = 'Confirmer ?'): boolean {
  if (btn.dataset.armed) return true;
  btn.dataset.armed = '1'; btn.textContent = label; btn.classList.add('armed');
  return false;
}

/** Message en bas d'écran avec « Annuler » pendant quelques secondes (D-178 : une suppression se rattrape). */
export function toastUndo(message: string, onUndo: () => void, ms = 6000): void {
  const t = document.createElement('div');
  t.className = 'toast toast-undo';
  t.setAttribute('role', 'status');
  const text = document.createElement('span');
  text.textContent = message;
  const b = document.createElement('button');
  b.type = 'button';
  b.textContent = 'Annuler';
  t.append(text, b);
  document.body.append(t);
  const timer = setTimeout(() => { t.classList.add('out'); setTimeout(() => t.remove(), 400); }, ms);
  b.addEventListener('click', () => { clearTimeout(timer); t.remove(); onUndo(); }, { once: true });
}

/** Message bref en bas d'écran. */
export function toast(message: string, kind: 'ok' | 'ko' = 'ok'): void {
  const t = document.createElement('div');
  t.className = `toast toast-${kind}`;
  t.setAttribute('role', 'status');
  t.textContent = message;
  document.body.append(t);
  const ms = Math.max(2600, message.length * 45);   // le temps de lire les messages longs
  setTimeout(() => t.classList.add('out'), ms);
  setTimeout(() => t.remove(), ms + 400);
}
